import { ElMessage } from 'element-plus';
import { defineStore } from 'pinia';

import { client, getErrorMessage } from '@/api';
import { CollectionGameDTO, CollectionGameModification, ModificationType } from '@/api/models';
import { SourceCollection } from '@/api/response/collections/SourceCollection';
import { EditingGame, useZingBrainStore } from './integrations/zing-brain.store';

function isValidPosition(position: unknown): position is number {
  return (
    typeof position === 'number' &&
    Number.isFinite(position) &&
    Number.isInteger(position) &&
    position >= 1
  );
}

function getOrderedActiveGames(games: EditingGame[]): EditingGame[] {
  return games
    .map((game, index) => ({ game, index }))
    .filter(({ game }) => game.modificationType !== 'DELETE')
    .sort((a, b) => {
      const ap = isValidPosition(a.game.position) ? a.game.position : Number.POSITIVE_INFINITY;
      const bp = isValidPosition(b.game.position) ? b.game.position : Number.POSITIVE_INFINITY;

      if (ap !== bp) {
        return ap - bp;
      }

      return a.index - b.index;
    })
    .map((g) => g.game);
}

function renumberActivePositions(games: EditingGame[]): void {
  const active = getOrderedActiveGames(games);
  for (let i = 0; i < active.length; i++) {
    active[i].position = i + 1;
  }
}

function moveActiveGameToPosition(
  games: EditingGame[],
  gameSlug: string,
  targetPosition?: number | null,
): void {
  const active = getOrderedActiveGames(games);
  const fromIndex = active.findIndex((g) => g.gameSlug === gameSlug);
  if (fromIndex === -1) {
    return;
  }

  const [item] = active.splice(fromIndex, 1);
  const maxPos = active.length + 1;
  const clamped = normalizePosition(targetPosition, maxPos);

  active.splice(clamped - 1, 0, item);

  for (let i = 0; i < active.length; i++) {
    active[i].position = i + 1;
  }
}

function normalizePosition(position: number | null | undefined, maxInclusive: number): number {
  const pos = position ?? maxInclusive;
  return Math.min(Math.max(1, pos), maxInclusive);
}

export type CollectionGamesState = {
  isGamesOpened: boolean;
  collectionId: string | null;
  loading: boolean;
  error: string | null;
  search: string | null;

  collectionGames: CollectionGameDTO[];
  editingGames: EditingGame[];
  sourceCollections: SourceCollection[];
  currentSource: string | undefined;
};

export const useCollectionGames = defineStore('collectionGames', {
  state: (): CollectionGamesState => ({
    isGamesOpened: false,
    collectionId: null,
    loading: false,
    error: null,
    search: null,
    collectionGames: [],
    editingGames: [],
    sourceCollections: [],
    currentSource: undefined,
  }),

  actions: {
    setLoading(value: boolean) {
      this.loading = value;
    },

    setError(value: string | null) {
      this.error = value;
    },

    closeCollectionGames() {
      this.isGamesOpened = false;
      this.collectionId = null;
      this.collectionGames = [];
      this.error = null;
    },

    setCurrentSource(currentSource: string | undefined): void {
      this.currentSource = currentSource;

      const zingBrainStore = useZingBrainStore();

      zingBrainStore.reset();
    },

    async initEditingGames(collectionId: string, search?: string): Promise<void> {
      this.setError(null);
      this.setLoading(true);

      try {
        // load all games without pagination
        const games: CollectionGameDTO[] = [];
        const pageSize = 100;

        let currentPage = 1;
        let totalPages: number | undefined;
        let hasMoreData = true;

        while (hasMoreData) {
          // eslint-disable-next-line no-await-in-loop
          const data = await client.collectionsApi.listCollectionGames(collectionId, {
            query: search ?? this.search ?? undefined,
            pageNumber: currentPage,
            pageSize,
          });

          // set total pages from the first request
          if (totalPages === undefined) {
            totalPages = data.pageCount || 1;
          }

          if (data.items && data.items?.length > 0) {
            games.push(...data.items);
          }

          const receivedItems = data.items?.length || 0;
          const isLastPage = currentPage >= (totalPages || 1);
          hasMoreData = receivedItems > 0 && !isLastPage;

          if (hasMoreData) {
            currentPage++;
          }
        }

        this.collectionGames = games;
        this.editingGames = games.map((game) => ({
          ...game,
        }));
      } catch (err) {
        const message = getErrorMessage(err, 'Error loading collection games for editing');
        ElMessage.error(message);
        this.setError(message);
      } finally {
        this.setLoading(false);
      }
    },

    addGame({ gameSlug, position }: { gameSlug: string; position: number }): boolean {
      if (!gameSlug) {
        ElMessage.error('Game is not selected');
        return false;
      }

      if (this.editingGames.some((g: EditingGame) => g.gameSlug === gameSlug)) {
        ElMessage.error('Game already exists!');
        return false;
      }

      const activeCount = this.editingGames.filter((g) => g.modificationType !== 'DELETE').length;
      const targetPosition = normalizePosition(position, activeCount + 1);

      this.editingGames.push({
        collectionId: this.collectionId as string,
        gameSlug: gameSlug as string,
        gameNameEn: '',
        aggregatorName: '',
        providerName: '',
        gameImageUrl: '',
        rtp: 0,
        position: targetPosition,
        isManual: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        modificationType: 'INSERT' as ModificationType,
      });

      moveActiveGameToPosition(this.editingGames, gameSlug, targetPosition);

      this.syncModificationTypes();
      this.editingGames = [...this.editingGames];

      return true;
    },

    updateGame(gameSlug: string, changes: { position?: number; isManual?: boolean }): void {
      const game = this.editingGames.find((g) => g.gameSlug === gameSlug);
      if (!game) return;
      if (game.modificationType === 'DELETE') return;

      if (changes.position !== undefined) {
        moveActiveGameToPosition(this.editingGames, gameSlug, changes.position);
      }

      if (changes.isManual !== undefined) {
        game.isManual = changes.isManual;
      }

      this.syncModificationTypes();
      this.editingGames = [...this.editingGames];
    },

    syncModificationTypes(): void {
      for (const game of this.editingGames) {
        if (game.modificationType !== 'INSERT' && game.modificationType !== 'DELETE') {
          const originalGame = this.collectionGames.find((g) => g.gameSlug === game.gameSlug);
          if (originalGame) {
            const hasChanges = game.position !== originalGame.position
              || game.isManual !== originalGame.isManual;
            game.modificationType = hasChanges ? ('UPDATE' as ModificationType) : undefined;
          }
        }
      }
    },

    removeGame(gameSlug: string): void {
      const gameIndex = this.editingGames.findIndex((g) => g.gameSlug === gameSlug);
      if (gameIndex === -1) return;

      const game = this.editingGames[gameIndex];
      if (game.modificationType === 'INSERT') {
        this.editingGames.splice(gameIndex, 1);
        renumberActivePositions(this.editingGames);

        this.syncModificationTypes();
        this.editingGames = [...this.editingGames];
        return;
      }

      if (game.modificationType === 'DELETE') {
        game.modificationType = undefined;

        const desiredPosition = isValidPosition(game.position)
          ? game.position
          : null;

        moveActiveGameToPosition(this.editingGames, gameSlug, desiredPosition);

        this.syncModificationTypes();
        this.editingGames = [...this.editingGames];
        return;
      }

      game.modificationType = 'DELETE';
      renumberActivePositions(this.editingGames);

      this.syncModificationTypes();
      this.editingGames = [...this.editingGames];
    },

    resetEditingGames(): void {
      this.editingGames = [];
    },

    /**
     * @deprecated
     * Method is left to backward compatibility,
     * instead of it useZingBrainStore.getRecommendations has to be used
     */
    async getRecommendations(params: Record<string, unknown>): Promise<void> {
      this.setError(null);
      this.setLoading(true);

      const zingBrainStore = useZingBrainStore();

      try {
        this.editingGames = await zingBrainStore.getRecommendations(
          this.collectionId || '',
          params,
        );
      } catch (err) {
        const message = getErrorMessage(err, 'Error getting games recommendations');

        ElMessage.error(message);

        this.setError(message);
      } finally {
        this.setLoading(false);
      }
    },

    async listCollectionSources(): Promise<SourceCollection[]> {
      this.setError(null);
      this.setLoading(true);

      this.sourceCollections = [];

      try {
        this.sourceCollections = await client.collectionsApi.listCollectionSources();
      } catch (err) {
        const message = getErrorMessage(err, 'Error getting source collections');

        ElMessage.error(message);

        this.setError(message);
      } finally {
        this.setLoading(false);
      }

      return this.sourceCollections;
    },

    getGamesModifications(): CollectionGameModification[] {
      return this.editingGames
        .filter((game) => Boolean(game.modificationType))
        .map(({ gameSlug, position, modificationType, isManual }) => ({
          gameSlug: gameSlug.toLowerCase(),
          position: position ?? undefined,
          modificationType: modificationType as ModificationType,
          isManual,
        }));
    },
  },
});
